import { Suspense } from 'react';
import Scene3D from './components/3d/Scene3D';
import HeroSection from './components/ui/HeroSection';
import LoadingScreen from './components/ui/LoadingScreen';
import useScrollAnimations from './hooks/useScrollAnimations';

function App() {
  useScrollAnimations();

  return (
    <div className='relative w-full min-h-screen bg-black'>
      {/* Loading Screen */}
      <LoadingScreen />

      {/* 3D Scene Background - Fixed with low z-index */}
      <div className='fixed inset-0 z-0'>
        <Suspense fallback={null}>
          <Scene3D />
        </Suspense>
      </div>

      {/* UI Content - Higher z-index with pointer events */}
      <main className='relative z-10 bg-black/40'>
        {/* Navigation */}
        <nav className='fixed top-0 left-0 right-0 z-50 p-6'>
          <div className='flex justify-center'>
            <div className='glass-effect rounded-full px-6 py-3'>
              <div className='flex space-x-4 text-sm'>
                <a
                  href='#hero'
                  className='text-white hover:text-blue-400 transition-colors duration-300'
                >
                  Home
                </a>
                <a
                  href='#about'
                  className='text-white hover:text-purple-400 transition-colors duration-300'
                >
                  About
                </a>
                <a
                  href='#technology'
                  className='text-white hover:text-cyan-400 transition-colors duration-300'
                >
                  Tech
                </a>
                <a
                  href='#journey'
                  className='text-white hover:text-emerald-400 transition-colors duration-300'
                >
                  Journey
                </a>
                <a
                  href='#innovation'
                  className='text-white hover:text-blue-400 transition-colors duration-300'
                >
                  Innovation
                </a>
                <a
                  href='#creative'
                  className='text-white hover:text-teal-400 transition-colors duration-300'
                >
                  Creative
                </a>
                <a
                  href='#experience'
                  className='text-white hover:text-rose-400 transition-colors duration-300'
                >
                  Experience
                </a>
              </div>
            </div>
          </div>
        </nav>

        <HeroSection />

        {/* About Section */}
        <section
          id='about'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-black/20 pointer-events-none' />
          <div className='relative z-10 container mx-auto'>
            <div className='grid md:grid-cols-2 gap-12 items-center'>
              <div className='about-card glass-effect rounded-2xl p-8'>
                <h2 className='text-4xl md:text-5xl font-bold mb-6'>
                  React Three Fiber
                  <span className='text-gradient block'>Excellence</span>
                </h2>
                <p className='text-lg text-gray-200 mb-6 leading-relaxed'>
                  Experience the power of React Three Fiber combined with modern
                  web technologies. Our immersive 3D environments showcase the
                  future of web development with smooth animations, interactive
                  particles, and stunning visual effects.
                </p>
                <div className='space-y-4'>
                  <div className='flex items-center space-x-3'>
                    <div className='w-2 h-2 bg-blue-500 rounded-full glow-effect'></div>
                    <span className='text-gray-200'>
                      React Three Fiber Integration
                    </span>
                  </div>
                  <div className='flex items-center space-x-3'>
                    <div className='w-2 h-2 bg-purple-500 rounded-full glow-effect'></div>
                    <span className='text-gray-200'>
                      Advanced Shader Materials
                    </span>
                  </div>
                  <div className='flex items-center space-x-3'>
                    <div className='w-2 h-2 bg-cyan-400 rounded-full glow-effect'></div>
                    <span className='text-gray-200'>
                      Interactive 3D Components
                    </span>
                  </div>
                </div>
              </div>

              <div className='relative'>
                <div className='absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-600/20 rounded-2xl blur-3xl'></div>
                <div className='relative glass-effect rounded-2xl p-8'>
                  <div className='grid grid-cols-2 gap-6 text-center'>
                    <div className='stat-item'>
                      <div className='text-3xl font-bold text-blue-400 mb-2 glow-effect'>
                        60+
                      </div>
                      <div className='text-gray-200'>FPS Performance</div>
                    </div>
                    <div className='stat-item'>
                      <div className='text-3xl font-bold text-purple-400 mb-2 glow-effect'>
                        2K+
                      </div>
                      <div className='text-gray-200'>Particles</div>
                    </div>
                    <div className='stat-item'>
                      <div className='text-3xl font-bold text-cyan-400 mb-2 glow-effect'>
                        100%
                      </div>
                      <div className='text-gray-200'>WebGL Compatible</div>
                    </div>
                    <div className='stat-item'>
                      <div className='text-3xl font-bold text-green-400 mb-2 glow-effect'>
                        3D
                      </div>
                      <div className='text-gray-200'>Immersive</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Section */}
        <section
          id='technology'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-black/10 pointer-events-none' />
          <div className='relative z-10 container mx-auto text-center'>
            <div className='glass-effect rounded-2xl p-12 max-w-4xl mx-auto'>
              <h2 className='text-4xl md:text-6xl font-bold mb-8'>
                Future
                <span className='text-gradient block'>Technology</span>
              </h2>
              <p className='text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed'>
                Experience the power of WebGL, Three.js, and React working in
                perfect harmony to create immersive digital experiences that
                blur the line between reality and imagination.
              </p>
              <div className='flex flex-wrap gap-4 justify-center'>
                <span className='glass-effect px-6 py-3 rounded-full text-lg'>
                  WebGL
                </span>
                <span className='glass-effect px-6 py-3 rounded-full text-lg'>
                  Three.js
                </span>
                <span className='glass-effect px-6 py-3 rounded-full text-lg'>
                  React Fiber
                </span>
                <span className='glass-effect px-6 py-3 rounded-full text-lg'>
                  GSAP
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Journey Section */}
        <section
          id='journey'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-black/15 pointer-events-none' />
          <div className='relative z-10 container mx-auto'>
            <div className='grid md:grid-cols-2 gap-16 items-center'>
              <div className='glass-effect rounded-2xl p-10'>
                <h2 className='text-4xl md:text-5xl font-bold mb-6'>
                  Embark on a
                  <span className='text-gradient block'>Digital Journey</span>
                </h2>
                <p className='text-lg text-gray-200 mb-6 leading-relaxed'>
                  Every scroll takes you deeper into a universe of
                  possibilities. Watch as particles dance, geometries float, and
                  reality bends to the rhythm of your exploration.
                </p>
                <div className='space-y-3'>
                  <div className='flex items-center space-x-3'>
                    <div className='w-2 h-2 bg-slate-300 rounded-full'></div>
                    <span className='text-gray-300'>
                      Infinite Scroll Experiences
                    </span>
                  </div>
                  <div className='flex items-center space-x-3'>
                    <div className='w-2 h-2 bg-slate-400 rounded-full'></div>
                    <span className='text-gray-300'>
                      Reactive 3D Environments
                    </span>
                  </div>
                </div>
              </div>

              <div className='text-center'>
                <div className='glass-effect rounded-2xl p-8 inline-block'>
                  <div className='text-6xl mb-4'>🌌</div>
                  <h3 className='text-2xl font-bold text-slate-200 mb-4'>
                    Endless Universe
                  </h3>
                  <p className='text-gray-400'>
                    Navigate through infinite digital landscapes
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Innovation Section */}
        <section
          id='innovation'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-gradient-to-br from-blue-900/10 via-transparent to-purple-900/10 pointer-events-none' />
          <div className='relative z-10 container mx-auto'>
            <div className='grid md:grid-cols-3 gap-8'>
              <div className='glass-effect rounded-2xl p-8 hover:bg-blue-500/10 transition-all duration-500 border-l-4 border-blue-400/50'>
                <div className='w-16 h-16 bg-gradient-to-r from-blue-400/20 to-blue-600/20 rounded-2xl flex items-center justify-center mb-6'>
                  <span className='text-3xl'>💫</span>
                </div>
                <h3 className='text-2xl font-bold mb-4 text-blue-100'>
                  Interactive Cosmos
                </h3>
                <p className='text-gray-300 leading-relaxed'>
                  Navigate through stellar environments that respond to your
                  every movement, creating a personalized journey through
                  digital space.
                </p>
              </div>

              <div className='glass-effect rounded-2xl p-8 hover:bg-purple-500/10 transition-all duration-500 border-l-4 border-purple-400/50'>
                <div className='w-16 h-16 bg-gradient-to-r from-purple-400/20 to-purple-600/20 rounded-2xl flex items-center justify-center mb-6'>
                  <span className='text-3xl'>🌟</span>
                </div>
                <h3 className='text-2xl font-bold mb-4 text-purple-100'>
                  Fluid Dynamics
                </h3>
                <p className='text-gray-300 leading-relaxed'>
                  Watch as thousands of particles flow and dance in perfect
                  harmony, creating mesmerizing patterns that evolve with your
                  exploration.
                </p>
              </div>

              <div className='glass-effect rounded-2xl p-8 hover:bg-cyan-500/10 transition-all duration-500 border-l-4 border-cyan-400/50'>
                <div className='w-16 h-16 bg-gradient-to-r from-cyan-400/20 to-cyan-600/20 rounded-2xl flex items-center justify-center mb-6'>
                  <span className='text-3xl'>⚡</span>
                </div>
                <h3 className='text-2xl font-bold mb-4 text-cyan-100'>
                  Real-time Magic
                </h3>
                <p className='text-gray-300 leading-relaxed'>
                  Experience smooth 60fps performance with real-time rendering
                  that brings every detail to life with stunning clarity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Creative Section */}
        <section
          id='creative'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-teal-900/10 pointer-events-none' />
          <div className='relative z-10 container mx-auto text-center'>
            <div className='max-w-5xl mx-auto'>
              <h2 className='text-4xl md:text-6xl font-bold mb-12'>
                <span className='text-emerald-200'>Creative</span>
                <span className='text-gradient block md:inline md:ml-4'>
                  Excellence
                </span>
              </h2>

              <div className='grid md:grid-cols-2 gap-12 items-center'>
                <div className='space-y-6'>
                  <div className='glass-effect rounded-xl p-6 text-left border border-emerald-400/20'>
                    <h4 className='text-xl font-semibold text-emerald-200 mb-3'>
                      Artistic Vision
                    </h4>
                    <p className='text-gray-300'>
                      Every element is crafted with artistic precision, blending
                      technical excellence with aesthetic beauty to create truly
                      memorable experiences.
                    </p>
                  </div>

                  <div className='glass-effect rounded-xl p-6 text-left border border-teal-400/20'>
                    <h4 className='text-xl font-semibold text-teal-200 mb-3'>
                      Innovative Design
                    </h4>
                    <p className='text-gray-300'>
                      Pushing the boundaries of what's possible in web
                      development through cutting-edge design patterns and
                      creative implementations.
                    </p>
                  </div>
                </div>

                <div className='glass-effect rounded-2xl p-8 border border-slate-400/20'>
                  <div className='text-center space-y-4'>
                    <div className='text-4xl font-bold text-emerald-300'>∞</div>
                    <h3 className='text-2xl font-bold text-white'>
                      Endless Possibilities
                    </h3>
                    <p className='text-gray-300'>
                      The only limit is imagination when technology meets
                      creativity in perfect harmony.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Experience Section */}
        <section
          id='experience'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-gradient-to-bl from-rose-900/10 via-transparent to-pink-900/10 pointer-events-none' />
          <div className='relative z-10 container mx-auto'>
            <div className='text-center mb-16'>
              <h2 className='text-4xl md:text-6xl font-bold mb-8'>
                <span className='text-rose-200'>Immersive</span>
                <span className='text-gradient block md:inline md:ml-4'>
                  Experience
                </span>
              </h2>
              <p className='text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed'>
                Step into a world where every interaction tells a story and
                every scroll unveils new wonders
              </p>
            </div>

            <div className='grid md:grid-cols-4 gap-6'>
              <div className='glass-effect rounded-xl p-6 text-center hover:bg-rose-500/10 transition-all duration-300'>
                <div className='text-3xl mb-4'>🎨</div>
                <h4 className='text-lg font-semibold text-rose-200 mb-2'>
                  Visual Poetry
                </h4>
                <p className='text-sm text-gray-400'>Art in motion</p>
              </div>

              <div className='glass-effect rounded-xl p-6 text-center hover:bg-pink-500/10 transition-all duration-300'>
                <div className='text-3xl mb-4'>🔮</div>
                <h4 className='text-lg font-semibold text-pink-200 mb-2'>
                  Future Tech
                </h4>
                <p className='text-sm text-gray-400'>Tomorrow, today</p>
              </div>

              <div className='glass-effect rounded-xl p-6 text-center hover:bg-violet-500/10 transition-all duration-300'>
                <div className='text-3xl mb-4'>✨</div>
                <h4 className='text-lg font-semibold text-violet-200 mb-2'>
                  Magic Moments
                </h4>
                <p className='text-sm text-gray-400'>Wonder in code</p>
              </div>

              <div className='glass-effect rounded-xl p-6 text-center hover:bg-indigo-500/10 transition-all duration-300'>
                <div className='text-3xl mb-4'>🚀</div>
                <h4 className='text-lg font-semibold text-indigo-200 mb-2'>
                  Beyond Limits
                </h4>
                <p className='text-sm text-gray-400'>Infinite potential</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final Section */}
        <section
          id='finale'
          className='min-h-screen flex items-center justify-center px-6 py-10 relative'
        >
          <div className='absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20 pointer-events-none' />
          <div className='relative z-10 container mx-auto text-center'>
            <div className='max-w-4xl mx-auto'>
              <h2 className='text-5xl md:text-7xl font-bold mb-8 opacity-90'>
                Welcome to the
                <span className='text-gradient block'>Future</span>
              </h2>
              <p className='text-2xl md:text-3xl text-gray-300 mb-12 leading-relaxed opacity-80'>
                Where imagination meets technology and dreams become digital
                reality
              </p>
              <div className='glass-effect rounded-2xl p-8 inline-block'>
                <p className='text-lg text-gray-400'>
                  Thank you for joining us on this journey through space and
                  time
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Performance Indicator */}
      <div className='fixed bottom-4 right-4 z-50 glass-effect rounded-lg p-3 text-xs'>
        <span className='text-gray-300'>Performance: </span>
        <span className='text-green-400 font-mono'>60 FPS</span>
      </div>
    </div>
  );
}

export default App;
